import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, FileText, Clock, ArrowRight, Target } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

const MandateCard = ({ mandate, index }) => {
  const deadline = daysUntil(mandate.mandate_deadline);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Link to={`/mandates/${mandate.slug}`} className="block group" data-testid={`mandate-card-${mandate.slug}`}>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#0066CC]/40 hover:shadow-lg transition-all h-full">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#0066CC] transition-colors">{mandate.name}</h3>
            <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0066CC] group-hover:translate-x-1 transition-all" />
          </div>
          <p className="text-sm text-slate-500 line-clamp-3 mb-5">{mandate.public_summary || `In-depth coverage of ${mandate.name}`}</p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {mandate.article_count} article{mandate.article_count !== 1 ? 's' : ''}</span>
            {deadline !== null && (
              <span className={`flex items-center gap-1 ${deadline < 90 ? 'text-amber-600 font-medium' : ''}`}>
                <Clock className="w-3.5 h-3.5" /> {deadline > 0 ? `${deadline} days to deadline` : 'Deadline passed'}
              </span>
            )}
            {mandate.mandate_deadline && (
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(mandate.mandate_deadline).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const MandatesPage = () => {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/mandates`)
      .then(r => r.json())
      .then(d => setMandates(d.mandates || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-12">
          <div className="flex items-center gap-2 text-sm text-[#0066CC] font-medium mb-4">
            <Target className="w-4 h-4" /> MANDATE INTELLIGENCE
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Tax mandate<br /><span className="text-[#0066CC]">deep dives.</span>
          </h1>
          <p className="text-base text-slate-500 max-w-2xl">
            In-depth coverage of specific tax mandates and country requirements.
            Regulatory analysis, technical guidance, FAQs, and practical readiness checklists — everything you need to prepare.
          </p>
        </motion.div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : mandates.length === 0 ? (
          <div className="text-center py-20">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">Coming soon</h3>
            <p className="text-sm text-slate-400">Mandate-specific content hubs are being prepared. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {mandates.map((m, i) => <MandateCard key={m.id} mandate={m} index={i} />)}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default MandatesPage;
